import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ExternalLink, MessageCircle, Lock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { Posit } from "@/types/posit";

interface SimpleEmbedFormProps {
  posit: Posit | null;
  embedConfig?: {
    theme?: "auto" | "light" | "dark";
    showBranding?: boolean;
    maxWidth?: string;
    borderRadius?: string;
    primaryColor?: string;
    domain?: string;
    placeholder?: string;
  };
  onInteraction?: (type: 'submit' | 'click') => void;
}

const SimpleEmbedForm = ({ posit, embedConfig = {}, onInteraction }: SimpleEmbedFormProps) => {
  const [question, setQuestion] = useState("");
  const [showGate, setShowGate] = useState(false);
  const { user } = useAuth();

  const {
    theme = "auto",
    showBranding = true,
    maxWidth = "100%",
    borderRadius = "12px",
    primaryColor = "#000000",
    domain = "unknown",
    placeholder = "Ask a question..."
  } = embedConfig;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!question.trim() || !posit) return;

    onInteraction?.('submit');

    // Free-question gate for unauthenticated users: allow 2 questions
    try {
      const key = `posit_free_q_count_${posit.id}`;
      const used = parseInt(localStorage.getItem(key) || '0', 10) || 0;
      if (!user && used >= 2) {
        setShowGate(true);
        return;
      }
      if (!user) {
        localStorage.setItem(key, String(used + 1));
      }
    } catch {}

    // Create the URL to redirect to with the question as a parameter
    const baseUrl = window.location.origin;
    const chatUrl = `${baseUrl}/chat/${posit.id}`;
    const params = new URLSearchParams();
    params.set('q', question.trim());
    window.open(`${chatUrl}?${params.toString()}`, '_blank');
  };

  const handleVisitPosit = () => {
    onInteraction?.('click');
    if (posit) {
      const baseUrl = window.location.origin;
      window.open(`${baseUrl}/chat/${posit.id}`, '_blank');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!posit) {
    return (
      <div 
        className="w-full p-6 bg-background border border-border rounded-lg"
        style={{ 
          maxWidth,
          borderRadius: borderRadius
        }}
      >
        <div className="text-center text-muted-foreground">
          Posit not found
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`w-full bg-background border border-border ${theme} text-foreground`}
      style={{ 
        maxWidth,
        borderRadius: borderRadius
      }}
    >
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <MessageCircle className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm line-clamp-2">{posit.title}</h3>
            {(posit.public_description || posit.posit_description) && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {posit.public_description || posit.posit_description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Input Form */}
      <div className="p-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex gap-2">
            <Input
              type="text"
              placeholder={placeholder}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1"
            />
            <Button 
              type="submit" 
              disabled={!question.trim()}
              style={{ backgroundColor: primaryColor }}
              className="text-white hover:opacity-90"
            >
              Ask
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Your question will open in a new tab for a full conversation experience.
          </p>
        </form>
      </div>

      {/* Branding Footer */}
      {showBranding && (
        <div className="p-3 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Ask anything about {posit.title}</span>
            <button
              onClick={handleVisitPosit}
              className="flex items-center gap-1 hover:text-foreground transition-colors"
            >
              <span>Powered by Posit</span>
              <ExternalLink className="h-2.5 w-2.5" />
            </button>
          </div>
        </div>
      )}

      {/* Free question gate modal */}
      <Dialog open={showGate} onOpenChange={setShowGate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-4 w-4" /> Continue the conversation
            </DialogTitle>
            <DialogDescription>
              You’ve used your 2 free questions. Sign in to keep chatting or upgrade for unlimited access.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Button onClick={() => (window.location.href = '/login')} className="flex-1">Sign in</Button>
            <Button variant="outline" onClick={() => (window.location.href = '/signup')} className="flex-1">Create account</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SimpleEmbedForm; 